import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import type { SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import Actions, { useSelector } from '../../../../Flux/index.js'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import type { State } from '../../../../Flux/Slice/index.js'
import { BlastSource } from '../../../../Model/Project/BlastSource.js'
import SinksGrid from '../SinksGrid.js'
import { groupByValue } from '../../SampleGrouping/Operations/GroupOperation.js'
import SavedViewsMenu from '../Persistence/SavedViewsMenu.js'
import { PrintButton } from '../PrintButton.js'
import { exportElementAsPng } from '../ExportPng.js'
import CubeDiagnosticsPanel from '../CubeDiagnosticsPanel.js'
import SourceSidebar from './SourceSidebar.js'
import ScopeBadge from './ScopeBadge.js'
import WorkspaceDataNotice from './WorkspaceDataNotice.js'
import CubeLoadingBadge from './CubeLoadingBadge.js'
import { AdjustmentsHorizontalIcon, ArrowLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { shallowEqual } from 'react-redux'
import type { ResumableSession } from './UseWorkspaceHydration.js'
import ResumeSessionBanner from './ResumeSessionBanner.js'
import AddChartTile from './AddChartTile.js'
import SpinQuickToggle from './SpinQuickToggle.js'
import { trackVizMenu } from '../../../../Services/Telemetry/index.js'
import { useGetAuthenticatedUser } from '../../../../Flux/Selector/index.js'
import { canUseSavedViews } from '../../../../Model/index.js'

/* generate a random id for new sources */
function randomSourceId(): SourceId {
  return `src:${Date.now()}-${Math.random().toString(36).slice(2, 7)}` as SourceId
}

export default function VizWorkspace({
  orderOrProjectId: projectId,
  resumable = null
}: {
  orderOrProjectId: string
  resumable?: ResumableSession | null
}) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const vizActions = Actions[visualizationSlice.name]
  const sourceIds = useSelector((s: typeof State) => Object.keys(s.visualization.sources) as SourceId[], shallowEqual)
  const sourceCount = sourceIds.length
  const scope = useSelector((s: typeof State) => s.visualization.scope)
  const savedViewsAccess = canUseSavedViews(useGetAuthenticatedUser())

  const handleBack = () => {
    if (!scope) return
    if (scope.kind === 'project') {
      navigate(`/projects/${scope.id}`)
    } else {
      navigate(`/order/${scope.id}`)
    }
  }

  useEffect(() => {
    dispatch(vizActions.loadTraits({ scopeId: projectId }))
  }, [projectId])

  const [activeId, setActiveId] = useState<SourceId | null>(null)
  const [collapseSources, setCollapseSources] = useState(true)

  const [showRemoteModal, setShowRemoteModal] = useState(false)
  const captureRef = React.useRef<HTMLDivElement>(null)

  const toggleAside = (show: boolean) => {
    setCollapseSources(!show)
  }

  // The sidebar shows nothing until a source is picked, so opening it must pick one.
  const customize = () => {
    if (activeId === null && sourceIds.length > 0) setActiveId(sourceIds[0])
    toggleAside(true)
  }

  // The open sidebar is the edit mode: chart chrome and the add tile only show while customizing.
  const editing = !collapseSources
  const addChartSourceId = activeId !== null && sourceIds.includes(activeId) ? activeId : sourceIds[0]

  const onExport = async () => {
    const node = captureRef.current
    if (!node) return
    await exportElementAsPng(node, `workspace.png`, { padding: 8 })
  }

  const addSource = useCallback(() => {
    /* add source with default config */
    const id = randomSourceId()
    dispatch(
      vizActions.createSource({
        sourceId: id,
        initial: {
          config: {
            kind: 'pipeline/v1',
            pipeline: {
              filters: [],
              groupBys: [{ operationKey: groupByValue.key, metaDataField: 'sampleMetadata.customerSampleName', config: {} }],
              projections: [],
              sortBys: []
            }
          },
          shared: { blastType: BlastSource.NCBI, selectedPath: [] },
          state: { selectedBucketKeys: [] },
          policy: { mode: 'block' }
        }
      })
    )
    setActiveId(id)
  }, [dispatch, vizActions])

  const removeSource = (id: string) => {
    dispatch(vizActions.removeSource({ sourceId: id }))
    setActiveId((prev) => (prev === id ? null : prev))
  }

  return (
    <div className="w-full h-full flex" data-testid="viz-workspace">
      {/* Sidebar with sources */}
      {!collapseSources && (
        <SourceSidebar
          addSource={addSource}
          removeSource={removeSource}
          activeId={activeId}
          toggleAside={toggleAside}
          setActiveId={setActiveId}
        />
      )}
      {collapseSources && (
        <aside className="h-full border-r bg-base-100 flex flex-col print:hidden m-auto justify-center">
          <button
            data-testid="viz-sidebar-expand"
            onClick={customize}
            title="Customize"
            className={'btn btn-xs h-full rounded-none'}
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </aside>
      )}

      {/* sink area */}
      <div className="flex-1 h-full overflow-auto bg-base-200">
        <div className="mx-auto px-3 py-2">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {scope && (
                <button
                  data-testid="viz-back"
                  className="btn btn-ghost btn-sm btn-square"
                  onClick={handleBack}
                  title={`Back to ${scope.kind === 'project' ? 'project' : 'order'}`}
                  aria-label={`Back to ${scope.kind === 'project' ? 'project' : 'order'}`}
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                </button>
              )}
              <h1 className="text-lg font-semibold whitespace-nowrap">Visualization Workspace</h1>
              <ScopeBadge fallbackId={projectId} />
            </div>
            <div className="flex items-center gap-2">
              {editing ? (
                <button
                  data-testid="viz-customize-exit"
                  className="btn btn-sm btn-primary"
                  title="Hide the editing controls. Your changes are kept."
                  onClick={() => toggleAside(false)}
                >
                  <XMarkIcon className="w-4 h-4" />
                  Exit customize
                </button>
              ) : (
                <button data-testid="viz-customize" className="btn btn-sm" onClick={customize}>
                  <AdjustmentsHorizontalIcon className="w-4 h-4" />
                  Customize
                </button>
              )}
              <SpinQuickToggle />
              <CubeLoadingBadge />
              <CubeDiagnosticsPanel />
              {savedViewsAccess && (
                <>
                  <button
                    data-testid="viz-views"
                    className="btn btn-sm"
                    onClick={() => {
                      trackVizMenu('open_save_load')
                      setShowRemoteModal(true)
                    }}
                  >
                    Views
                  </button>
                  {showRemoteModal && <SavedViewsMenu onClose={() => setShowRemoteModal(false)} />}
                </>
              )}
              <PrintButton onCapture={() => onExport()} />
            </div>
          </div>

          {resumable && <ResumeSessionBanner session={resumable} />}

          {sourceCount === 0 ? (
            <div className="rounded-lg bg-base-100 shadow p-8 text-sm opacity-80">No sources yet. Use “+ Add” to create one.</div>
          ) : (
            <div className="flex flex-col gap-8" ref={captureRef}>
              <WorkspaceDataNotice scopeKind={scope?.kind} />
              <SinksGrid activeSourceId={editing ? activeId : null} editing={editing} />
            </div>
          )}
          {editing && addChartSourceId && (
            <div className="mt-4 print:hidden">
              <AddChartTile sourceId={addChartSourceId} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
